import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import { collection, query, onSnapshot, addDoc, deleteDoc, doc, where, orderBy, serverTimestamp, getDocs, DocumentData } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { Product } from '../../types';
import { Package, Plus, ExternalLink, Trash2, Search, Filter, Loader2, CheckCircle, XCircle } from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';
import { css, Global } from '@emotion/react';

interface NewProduct {
  name: string;
  platform: string;
  productLink: string;
  price: string;
  description: string;
  imageUrl: string;
  asinId: string;
}

const ProductListing: React.FC = () => {
  const { userProfile } = useAuth();
  const [products, setProducts] = useState<Product[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPlatform, setFilterPlatform] = useState('');
  const [newProduct, setNewProduct] = useState<NewProduct>({
    name: '',
    platform: '',
    productLink: '',
    price: '',
    description: '',
    imageUrl: '',
    asinId: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Helper function to safely convert Firestore timestamp to Date
  const getDateFromTimestamp = (timestamp: unknown): Date => {
    if (!timestamp) return new Date();
    if (timestamp instanceof Date) return timestamp;
    if (typeof timestamp === 'object' && timestamp !== null) {
      if ('toDate' in timestamp) {
        return (timestamp as { toDate: () => Date }).toDate();
      }
      if ('seconds' in timestamp) {
        return new Date((timestamp as { seconds: number }).seconds * 1000);
      }
    }
    try {
      return new Date(timestamp as string | number);
    } catch (e) {
      console.error('Error parsing date:', e);
      return new Date();
    }
  };

  useEffect(() => {
    if (!userProfile) return;

    // Query only products added by the current user
    const q = query(
      collection(db, 'products'),
      where('addedBy', '==', userProfile.uid)
      // Temporarily removed orderBy to test if that's causing the issue
      // orderBy('createdAt', 'desc')
    );
    console.log('Fetching products for user:', userProfile.uid);
    
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const productsData: Product[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        productsData.push({ 
          id: doc.id, 
          ...data,
          createdAt: getDateFromTimestamp(data.createdAt),
          updatedAt: getDateFromTimestamp(data.updatedAt)
        } as Product);
      });
      setProducts(productsData);
    });

    return () => unsubscribe();
  }, [userProfile]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setNewProduct(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userProfile) {
      toast.error('Please sign in to add products');
      return;
    }
    
    // Basic validation
    if (!newProduct.name || !newProduct.platform || !newProduct.productLink || !newProduct.price) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    const loadingToast = toast.loading('Adding your product...');

    try {
      const productData = {
        name: newProduct.name.trim(),
        platform: newProduct.platform as 'Amazon' | 'Flipkart' | 'Myntra' | 'Other',
        productLink: newProduct.productLink.trim(),
        price: parseFloat(newProduct.price),
        description: newProduct.description?.trim() || '',
        imageUrl: newProduct.imageUrl?.trim() || '',
        asinId: newProduct.asinId?.trim() || '',
        addedBy: userProfile.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      console.log('Attempting to add product:', productData);
      
      // Add the document and get the reference
      const docRef = await addDoc(collection(db, 'products'), productData);
      console.log('Product added with ID: ', docRef.id);
      
      // Reset form
      setNewProduct({
        name: '',
        platform: '',
        productLink: '',
        price: '',
        description: '',
        imageUrl: '',
        asinId: ''
      });
      setShowAddForm(false);
      
      // Force a re-fetch of products
      const q = query(
        collection(db, 'products'),
        where('addedBy', '==', userProfile.uid),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      console.log('Current products after add:', querySnapshot.docs.map((doc: { id: string; data: () => DocumentData }) => ({
        id: doc.id,
        ...doc.data()
      })));
      
      toast.dismiss(loadingToast);
      toast.success('Product added successfully!');
    } catch (error) {
      console.error('Error adding product:', error);
      toast.dismiss(loadingToast);
      toast.error('Failed to add product. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteProduct = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      await deleteDoc(doc(db, 'products', id));
      toast.success('Product deleted successfully');
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Failed to delete product');
    }
  };

  const filteredProducts = products.filter(product => {
    const searchTermLower = searchTerm.toLowerCase();
    const productName = product.name?.toLowerCase() || '';
    const productPlatform = product.platform?.toLowerCase() || '';
    const productDescription = product.description?.toLowerCase() || '';
    
    const matchesSearch = productName.includes(searchTermLower) || 
                         productDescription.includes(searchTermLower) ||
                         productPlatform.includes(searchTermLower);
    
    const matchesPlatform = !filterPlatform || product.platform === filterPlatform;
    
    return matchesSearch && matchesPlatform;
  });

  // Get unique platforms for filter dropdown
  const platforms = Array.from(new Set(products.map(p => p.platform))).filter(Boolean) as string[];

  // Animation variants
  const formVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        type: 'spring' as const,
        stiffness: 300,
        damping: 20
      }
    }
  };

  return (
    <DashboardLayout>
      <Toaster position="top-right" />
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800 flex items-center">
            <Package className="mr-2 h-8 w-8" />
            My Products
          </h1>
          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setShowAddForm(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center"
            disabled={showAddForm}
          >
            <Plus className="mr-1 h-5 w-5" /> Add Product
          </motion.button>
        </div>

        {/* Search and Filter */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <select
              value={filterPlatform}
              onChange={(e) => setFilterPlatform(e.target.value)}
              className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Platforms</option>
              {platforms.map(platform => (
                <option key={platform} value={platform}>{platform}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Add Product Form */}
        <AnimatePresence>
          {showAddForm && (
            <motion.div 
              variants={formVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="bg-white rounded-lg shadow-lg p-6 mb-8 border border-gray-100"
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-800">Add New Product</h2>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowAddForm(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <XCircle className="h-6 w-6" />
                </motion.button>
              </div>
              
              <form onSubmit={handleAddProduct} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Product Name *</label>
                    <input
                      type="text"
                      value={newProduct.name}
                      onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Platform *</label>
                    <select
                      value={newProduct.platform}
                      onChange={(e) => setNewProduct({...newProduct, platform: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
                      required
                    >
                      <option value="">Select Platform</option>
                      <option value="Amazon">Amazon</option>
                      <option value="Flipkart">Flipkart</option>
                      <option value="Myntra">Myntra</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Product Link *</label>
                    <input
                      type="url"
                      value={newProduct.productLink}
                      onChange={(e) => setNewProduct({...newProduct, productLink: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Price (₹) *</label>
                    <input
                      type="number"
                      value={newProduct.price}
                      onChange={(e) => setNewProduct({...newProduct, price: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
                      step="0.01"
                      min="0"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ASIN/Product ID</label>
                    <input
                      type="text"
                      value={newProduct.asinId}
                      onChange={(e) => setNewProduct({...newProduct, asinId: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
                      placeholder="Optional"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Image URL</label>
                    <input
                      type="url"
                      value={newProduct.imageUrl}
                      onChange={(e) => setNewProduct({...newProduct, imageUrl: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
                      placeholder="Optional"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={newProduct.description}
                    onChange={(e) => setNewProduct({...newProduct, description: e.target.value})}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
                    placeholder="Add a brief description (optional)"
                  />
                </div>
                <div className="flex justify-end space-x-3 pt-2">
                  <motion.button
                    type="button"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setShowAddForm(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    type="submit"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`px-4 py-2 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200 ${
                      isSubmitting 
                        ? 'bg-blue-400 cursor-not-allowed' 
                        : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
                    }`}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center">
                        <Loader2 className="animate-spin mr-2 h-4 w-4" />
                        Adding...
                      </span>
                    ) : (
                      <span className="flex items-center">
                        <CheckCircle className="mr-1 h-5 w-5" />
                        Add Product
                      </span>
                    )}
                  </motion.button>
                </div>
              </form>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProducts.length === 0 ? (
            <div className="col-span-full text-center py-12 bg-white rounded-xl shadow-sm p-8">
              <Package className="mx-auto h-16 w-16 text-blue-500 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {searchTerm || filterPlatform ? 'No products found' : 'No products yet'}
              </h3>
              <p className="text-gray-500 mb-6">
                {searchTerm || filterPlatform 
                  ? 'Try adjusting your search or filter criteria.'
                  : 'Add your first product to get started.'
                }
              </p>
              <button
                onClick={() => setShowAddForm(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Product
              </button>
            </div>
          ) : (
            <AnimatePresence>
              {filteredProducts.map((product, index) => (
                <motion.div
                  key={product.id}
                  className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden border border-gray-100"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{
                    delay: index * 0.05,
                    type: 'spring',
                    stiffness: 300,
                    damping: 20
                  }}
                  whileHover={{ y: -5 }}
                >
                  {/* Product Image */}
                  <div className="relative h-48 bg-gray-50 overflow-hidden">
                    {product.imageUrl ? (
                      <img
                        src={product.imageUrl}
                        alt={product.name}
                        className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = 'https://via.placeholder.com/400x300?text=No+Image';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
                        <Package className="h-16 w-16 text-gray-300" />
                      </div>
                    )}
                    {/* Platform Badge */}
                    <div className={`absolute top-3 right-3 px-2.5 py-1 rounded-full text-xs font-medium ${
                      product.platform === 'Amazon' 
                        ? 'bg-yellow-100 text-yellow-800' 
                        : product.platform === 'Flipkart' 
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-800'
                    }`}>
                      {product.platform}
                    </div>
                  </div>

                  {/* Product Info */}
                  <div className="p-5">
                    <div className="flex justify-between items-start">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-gray-900 truncate">
                          {product.name}
                        </h3>
                        {product.description && (
                          <p className="mt-1 text-sm text-gray-500 line-clamp-2">
                            {product.description}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Price and Actions */}
                    <div className="mt-4 flex items-center justify-between">
                      <div>
                        <span className="text-2xl font-bold text-gray-900">
                          ₹{typeof product.price === 'number' ? product.price.toFixed(2) : parseFloat(product.price).toFixed(2)}
                        </span>
                        {product.asinId && (
                          <span className="ml-2 text-xs text-gray-500">
                            ID: {product.asinId}
                          </span>
                        )}
                      </div>
                      <div className="flex space-x-2">
                        <a
                          href={product.productLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 text-gray-500 hover:text-blue-600 transition-colors"
                          title="View Product"
                        >
                          <ExternalLink className="h-5 w-5" />
                        </a>
                        <button
                          onClick={() => handleDeleteProduct(product.id)}
                          className="p-2 text-gray-500 hover:text-red-600 transition-colors"
                          title="Delete Product"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          )}
        </div>

        {/* Stats */}
        {products.length > 0 && (
          <div className="mt-8 bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Statistics</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{products.length}</div>
                <div className="text-sm text-gray-600">Total Products</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {Array.from(new Set(products.map(p => p.platform))).length}
                </div>
                <div className="text-sm text-gray-600">Platforms</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600">
                  {products.filter((p) => getDateFromTimestamp(p.createdAt).getTime() > Date.now() - 30 * 24 * 60 * 60 * 1000).length}
                </div>
                <div className="text-sm text-gray-600">Added This Month</div>
              </div>
            </div>
          </div>
        )}
      </div>

      <Global
        styles={css`
          /* Add smooth transitions for all elements */
          * {
            transition: background-color 0.2s ease, border-color 0.2s ease;
          }
          
          /* Custom scrollbar */
          ::-webkit-scrollbar {
            width: 6px;
            height: 6px;
          }
          
          ::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 3px;
          }
          
          ::-webkit-scrollbar-thumb {
            background: #c1c1c1;
            border-radius: 3px;
          }
          
          ::-webkit-scrollbar-thumb:hover {
            background: #a1a1a1;
          }
          
          /* Animation for success message */
          .toast-success {
            background: #10B981 !important;
            color: white !important;
            font-weight: 500;
            border-radius: 8px !important;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          }
          
          /* Animation for error message */
          .toast-error {
            background: #EF4444 !important;
            color: white !important;
            font-weight: 500;
            border-radius: 8px !important;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          }
          
          /* Animation for loading state */
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
          
          .animate-pulse {
            animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
          }
        `}
      />
    </DashboardLayout>
  );
};

export default ProductListing;